import {translate} from "../../../../../components/translate/translate.jsx";
import ReportUtils from "../../../../../utils/report-utils.js";

const ignoredFields = ["schedule-garrison_message"];

const regex = /^\d{4}-\d{2}-\d{2}$/;
const weekdaysList = {
    1: "Segunda-feira",
    2: "Terça-feira",
    3: "Quarta-feira",
    4: "Quinta-feira",
    5: "Sexta-feira",
    6: "Sábado",
    7: "Domingo",
};

const Row = ({label, value, newValue, oldValue, isUpdate = false}) => {
    const currentValue = isUpdate ? (newValue ?? value) : value;

    const hasContent = (val) => {
        if (val === null || val === undefined) return false;
        if (val === "") return false;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
    };

    if (!hasContent(currentValue) && !hasContent(oldValue)) return null;

    return (
        <tr className="text-left">
            <td>{label}</td>
            <td className={'break-all! whitespace-normal'}>{ScheduleGarrisonUtils.formatValue(currentValue)}</td>

            {isUpdate && (
                <td className={'break-all! whitespace-normal'}>{ScheduleGarrisonUtils.formatValue(oldValue)}</td>
            )}
        </tr>
    );
};

const clearValueCommas = (value) => {
    const values = Array.isArray(value) ? value : String(value).split(",");

    return values
        .map(item => String(item).trim())
        .filter(item => item.length > 0)
        .join(", ");
};

const ScheduleGarrisonUtils = {

    formatValue (value) {
        if (value === null || value === undefined) return "";

        if (typeof value === "boolean") return value ? "Sim" : "Não";

        if (Array.isArray(value)) return clearValueCommas(value);

        if (typeof value === "string") {
            const formatted = clearValueCommas(value);
            if (regex.test(formatted)) return ReportUtils.formatDateOnly(formatted);
            return translate(formatted);
        }

        return clearValueCommas(ReportUtils.formatObjectString(value));
    },

    isDiffObject (value) {
        return (value && typeof value === "object" && !Array.isArray(value) && (Object.hasOwn(value, "old") || Object.hasOwn(value, "new")));
    },

    getNew (value) {
        return this.isDiffObject(value) ? value.new : value;
    },

    getOld (value) {
        return this.isDiffObject(value) ? value.old : undefined;
    },

    splitDiff (value) {
        if (!this.isDiffObject(value)) {
            return {
                current: value ?? {},
                previous: {}
            };
        }

        return {
            current: value.new ?? {},
            previous: value.old ?? {}
        };
    },

    formatWeekdays (weekdays) {
        weekdays = this.getNew(weekdays);
        if (!weekdays) return "";

        if (typeof weekdays === "string") {
            try {
                weekdays = JSON.parse(weekdays);
            } catch {
                weekdays = [];
            }
        }

        if (!Array.isArray(weekdays)) return "";
        return weekdays.map(day => weekdaysList[day] ?? day).join(", ");
    },

    formatPeriod (start, end) {
        if (!start && !end) return "";
        return `${start ? `${ReportUtils.formatDateOnly(start)}` : ''} ${start && end ? " à " : ""} ${end ? `${ReportUtils.formatDateOnly(end)}` : ''}`;
    },

    formatArray (value, separator = ", ") {
        value = this.getNew(value);

        if (!Array.isArray(value)) return "";
        return value.join(separator);
    },

    formatGroupFunctions (list = []) {
        list = this.getNew(list);

        if (!Array.isArray(list)) return "";
        return list.map(translate).join(", ");
    },

    hasVisibleFields (data) {
        if (!data) return false;

        return Object.keys(data).some(key => {
            if (ignoredFields.includes(key)) return false;

            const value = data[key];

            if (value === null || value === undefined) return false;

            if (Array.isArray(value)) return value.length > 0;

            if (typeof value === "object") {
                if (this.isDiffObject(value)) {
                    return value.old !== undefined || value.new !== undefined;
                }

                return Object.keys(value).length > 0;
            }

            return true;
        });
    },

    getAttachments (attachments, isUpdate) {
        if (!attachments) return [];

        if (!isUpdate) return Array.isArray(attachments) ? attachments : Object.values(attachments);

        if ("old" in attachments || "new" in attachments) {
            const oldAttachments = attachments.old ?? {};
            const newAttachments = attachments.new ?? {};

            return Object.keys({
                ...oldAttachments,
                ...newAttachments
            }).map(id => ({
                old: oldAttachments[id] ?? {},
                new: newAttachments[id] ?? {}
            }));
        }

        if (Array.isArray(attachments) && attachments.length === 2 && typeof attachments[0] === "object" && attachments[1] === "") {
            return Object.values(attachments[0]).map(item => ({
                old: item,
                new: {}
            }));
        }

        if (!Array.isArray(attachments) && typeof attachments === "object") return Object.values(attachments);

        return attachments;
    },

    getActivities (activities, isUpdate) {
        if (!activities) return [];

        if (!isUpdate) return Object.entries(activities);

        return Object.entries(activities).map(([id, activity]) => {

            if ("old" in activity || "new" in activity) {
                return [
                    id,
                    {
                        old: activity.old || {},
                        new: activity.new || {}
                    }
                ];
            }

            return [id, activity];
        });
    },

    Row
};

export default ScheduleGarrisonUtils;